// Live preview of a chat-mode email_document_templates row. The source is a
// raw HTML body fragment authored against the chat slicer contract (sections +
// data-link-zone anchors) with $brand.* / [NM:*] merge tags embedded inline.
// We resolve $brand tags client-side against the active brand and drop the
// result into a sandboxed iframe via composeChatIframeHtml — the same helper
// the chat editor itself uses, so card thumbnails are pixel-identical to what
// the user sees when they open the template in /chat.
//
// Sizing mirrors TemplateDocumentPreview's non-interactive (card) mode: the
// iframe renders at its natural 600px width and is transform-scaled to fit
// displayWidth × maxDisplayHeight. We post a one-shot height message from
// the iframe on load so the outer wrapper can apply fit-both scaling without
// reading the cross-origin content document.

import { useTranslation } from 'react-i18next';
import { useRef, useMemo, useState, useEffect } from 'react';
import {
  resolveMergeTags,
  type BrandResolverContext,
  mergeBrandFontsIntoChatHtml,
} from '@nexmail/shared-util/email-builder';
import {
  composeChatIframeHtml,
  bodyHtmlContainsHeader,
  bodyHtmlContainsFooter,
} from '@/components/email-generation/chat-iframe-compose';

import Box from '@mui/material/Box';

interface Props {
  sourceHtml: string;
  brand: BrandResolverContext | undefined;
  // Natural authoring width matches every other template surface (600).
  displayWidth?: number;
  maxDisplayHeight?: number;
  // When true, the preview measures its parent's content-box height and
  // scales the iframe so the entire email fits inside that box (fit-both,
  // not fit-width). Use in modals where the surrounding chrome is fixed and
  // an overflowing preview would bleed behind the title/action bars.
  fillContainerHeight?: boolean;
  // When true, the outer box is scrollable and ctrl/cmd + wheel (trackpad
  // pinch) adjusts a zoom multiplier on top of the fit scale. Pointer events
  // pass through the iframe to the wrapper so scroll/zoom land here, not in
  // the iframe document. Card thumbnails leave this off.
  interactive?: boolean;
  // Optional default header/footer HTML. When provided (and the body doesn't
  // already own header/footer sections), splice them around the body so the
  // preview shows the email composed end-to-end. Cards skip this; the dialog
  // passes them in.
  headerHtml?: string | null;
  footerHtml?: string | null;
}

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
// Default zoom is a touch above the fit-to-container scale so opening the
// modal lands at a comfortable reading size rather than a tiny aerial view.
// The user can pinch out if they want the full picture.
const DEFAULT_ZOOM = 1.35;

const NATURAL_EMAIL_WIDTH = 600;

export function TemplateChatPreview({
  sourceHtml,
  brand,
  displayWidth = 480,
  maxDisplayHeight,
  fillContainerHeight = false,
  interactive = false,
  headerHtml = null,
  footerHtml = null,
}: Props) {
  const { t } = useTranslation('templates');
  // Resolve $brand.* once per (sourceHtml, brand). [NM:*] tags stay
  // untouched and render as their literal text in the iframe — that's the
  // intended behavior in the editor as well; ESP rewrites at send time.
  // Then merge the brand's heading/body fonts into each inline `font-family:`
  // so hardcoded template typefaces (Playfair, Inter, Anton, etc.) get the
  // brand font prepended as the new primary. Inline rules beat any iframe
  // stylesheet, so this rewrite is the only way the preview reflects the
  // brand's typography. Mirrors the /use endpoint's apply-time bake — what
  // the user sees here equals what gets persisted when they click Use.
  const resolvedHtml = useMemo(() => {
    const merged = brand
      ? resolveMergeTags(sourceHtml, brand, undefined)
      : sourceHtml;
    return mergeBrandFontsIntoChatHtml(merged, {
      heading: brand?.fontByUsage?.heading,
      body: brand?.fontByUsage?.body,
    });
  }, [sourceHtml, brand]);

  // composeChatIframeHtml expects body html + optional header/footer chrome.
  // Cards pass null for both (visual focus on the template). The modal passes
  // the default sections so the user sees the email composed end-to-end.
  // Per-side guards: if the body already owns its own header section we skip
  // splicing the default header (but still splice the footer), and the same
  // the other way — a one-sided body should not suppress both defaults.
  const srcDoc = useMemo(() => composeChatIframeHtml({
      headerHtml: bodyHtmlContainsHeader(resolvedHtml) ? null : headerHtml,
      footerHtml: bodyHtmlContainsFooter(resolvedHtml) ? null : footerHtml,
      bodyHtml: resolvedHtml,
      bodyBackgroundColor: brand?.colorByUsage?.background ?? '#ffffff',
      bodyFontFamily: brand?.fontByUsage?.body,
    }), [resolvedHtml, brand, headerHtml, footerHtml]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [contentHeight, setContentHeight] = useState<number>(0);
  const [parentHeight, setParentHeight] = useState<number>(0);
  const [zoom, setZoom] = useState<number>(DEFAULT_ZOOM);

  // Reset zoom when the underlying template changes — otherwise switching
  // templates in the same modal would inherit the previous gesture state.
  useEffect(() => {
    setZoom(DEFAULT_ZOOM);
  }, [sourceHtml]);

  // Trackpad pinch fires wheel with ctrlKey:true (Chrome/Safari). Use a
  // non-passive native listener so we can preventDefault and stop the
  // browser's page zoom from kicking in. Plain wheel (no modifier) is left
  // alone so the outer Box scrolls normally.
  useEffect(() => {
    if (!interactive) return;
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      setZoom((z) => {
        const next = z * Math.exp(-e.deltaY * 0.01);
        return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [interactive]);

  // Watch the parent's content-box height for fillContainerHeight mode. We
  // observe the parent rather than self because we want to know how much
  // vertical space we have to shrink into; observing self would feed back
  // into the size we set.
  useEffect(() => {
    if (!fillContainerHeight) return;
    const el = containerRef.current?.parentElement;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry && entry.contentRect) setParentHeight(entry.contentRect.height);
    });
    ro.observe(el);
    const cs = window.getComputedStyle(el);
    const padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
    setParentHeight(el.clientHeight - padY);
    return () => ro.disconnect();
  }, [fillContainerHeight]);

  // Sandboxed iframes can be same-origin via the `allow-same-origin` sandbox
  // flag, which lets us read documentElement.scrollHeight directly. The chat
  // editor uses the same trick. Re-measure on load and on a short polling
  // window so late-loading images don't leave the iframe at its initial
  // height.
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    let cancelled = false;
    let ticks = 0;
    let last = 0;
    const measure = () => {
      if (cancelled) return;
      const doc = iframe.contentDocument;
      const h = doc?.documentElement?.scrollHeight ?? 0;
      if (h && h !== last) {
        last = h;
        setContentHeight(h);
      }
      ticks++;
      if (ticks < 30) {
        setTimeout(measure, 120);
      }
    };
    const onLoad = () => {
      ticks = 0;
      measure();
    };
    iframe.addEventListener('load', onLoad);
    return () => {
      cancelled = true;
      iframe.removeEventListener('load', onLoad);
    };
  }, [srcDoc]);

  // Fit-both: width-scale first, then clamp by maxDisplayHeight (card mode)
  // or the measured parent content-box height (fillContainerHeight mode) so
  // the full email always fits inside the available space. Zoom is a user
  // multiplier on top of the fit scale, only applied in interactive mode.
  const widthScale = displayWidth / NATURAL_EMAIL_WIDTH;
  const heightCap = fillContainerHeight ? parentHeight : maxDisplayHeight;
  const fitScale =
    heightCap && contentHeight > 0
      ? Math.min(widthScale, heightCap / contentHeight)
      : widthScale;
  const effectiveScale = interactive ? fitScale * zoom : fitScale;
  const scaledHeight = (contentHeight || 0) * effectiveScale;
  const scaledWidth = NATURAL_EMAIL_WIDTH * effectiveScale;
  const outerHeight = fillContainerHeight
    ? parentHeight || scaledHeight
    : (maxDisplayHeight ?? scaledHeight ?? 480);

  // When interactive and zoomed in, the scaled content can exceed the outer
  // box on either axis — overflow:auto lets the user scroll to the rest.
  // When not interactive (cards) keep overflow hidden so the thumbnail can't
  // jitter scrollbars in.
  const overflow = interactive ? 'auto' : 'hidden';
  // Center content when it fits, scroll freely when it overflows (zoomed in).
  // `safe center` is the key: with plain `center` and overflow, the start of
  // the overflow becomes unreachable — you can't scroll up/left to the top
  // edge of the email. `safe` falls back to start alignment in the overflow
  // direction so the canvas pans both ways, matching the chat editor feel.
  return (
    <Box
      ref={containerRef}
      sx={{
        width: displayWidth,
        height: outerHeight || 480,
        overflow,
        bgcolor: brand?.colorByUsage?.background ?? '#ffffff',
        borderRadius: 1,
        boxShadow: '0 4px 18px rgba(0,0,0,0.10)',
        position: 'relative',
        display: 'flex',
        alignItems: 'safe center',
        justifyContent: 'safe center',
        // Cards stay non-interactive (tile thumbnails). Interactive mode lets
        // wheel/scroll/pinch reach the wrapper.
        pointerEvents: interactive ? 'auto' : 'none',
        // Hint trackpads to send pinch as ctrl+wheel rather than browser
        // pinch-zoom while the cursor is over the preview.
        touchAction: interactive ? 'pan-x pan-y' : undefined,
      }}
    >
      <Box
        sx={{
          width: scaledWidth,
          height: scaledHeight || (contentHeight || 1) * effectiveScale,
          flexShrink: 0,
        }}
      >
        <iframe
          ref={iframeRef}
          title={t('templateChatPreview.iframeTitle', 'Template preview')}
          srcDoc={srcDoc}
          // allow-same-origin so we can measure scrollHeight from the parent;
          // no allow-scripts because chat templates are static HTML/CSS only.
          sandbox="allow-same-origin"
          style={{
            width: NATURAL_EMAIL_WIDTH,
            height: contentHeight || 600,
            border: 'none',
            transform: `scale(${effectiveScale})`,
            transformOrigin: 'top left',
            display: 'block',
            // Pointer-events off so wheel/pinch events target the outer Box
            // (we want to drive scroll + zoom there, not in the iframe doc).
            pointerEvents: 'none',
          }}
        />
      </Box>
    </Box>
  );
}
